import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner';
import {
  generateFilterOptions,
  validateCancelSamplesSelection,
  validateManageSamplesSelection,
} from '@/components/Samples/Samples.utils';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { UseTableProps } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import { updateSettings } from '@/components/UI/Table/Reusable/Cache.utils';
import type { TanStackColumns } from '@/components/UI/Table/TableComponent.model';
import { errorToast, successToast } from '@/helpers';
import { _isNotEmpty, _notNil } from '@/littledash';
import type { MetadataFieldWithValue } from '@/model/Metadata.model';
import type { Sample } from '@/model/Sample.model';
import type { State } from '@/model/State.model';
import type { Study } from '@/model/Study.model';
import { ApiService } from '@/support/ApiService';
import * as Auth from '@/support/auth';
import { useFetchCollection } from '@/support/Hooks/fetch';
import { notAborted } from '@/support/Hooks/fetch/useAbortController';
import useMountedState from '@/support/Hooks/fetch/useMountedState';
import Http from '@/support/http';
import { api as apiRoute, web as webRoute } from '@/support/route';
import { modalAction } from '@/utils/modal';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { createSelector } from '@reduxjs/toolkit';
import { featuresSelector } from '@/support/Selectors';
import { buildColumns } from './Samples.utils';
import { createMultiSelectDoubleEncodingProcessor } from '@/utils/TableFilter.utils';
import type { components } from '@/generated/internal-api/openapi-types';
import type { ActionProps } from '@/components/UI/SelectDropDown/Menus/ActionList';

type SampleApiId = components['schemas']['SampleApiId.schema'];
type SampleBulkUpdateStatus = components['schemas']['SampleBulkUpdateStatus.schema'];
type SampleBulkUpdateStatusV1 = components['schemas']['SampleBulkUpdateStatusV1.schema'];

const featureSelector = createSelector([featuresSelector], (features: any) => ({
  reasonForChange: features?.reason_for_change ?? false,
  plannedSamples: features?.planned_samples ?? false,
}));

const SamplesTable: React.FC = () => {
  const { id: studyId } = useParams<{ id: string }>();
  const [columns, setColumns] = useState<TanStackColumns<Sample>>([]);
  const [study, setStudy] = useState<Study>();
  const [studyLoading, setStudyLoading] = useState<boolean>();
  // Required as this component is utilised both in Study context and without
  const isStudy = _isNotEmpty(studyId);
  const history = useHistory();
  const dispatch = useDispatch();
  const isMounted = useMountedState();

  const { users, settings, reasonForChange, plannedSamples } = useSelector((state: State) => ({
    users: state.team?.team?.users ?? [],
    settings: state.ui?.settings,
    ...featureSelector(state),
  }));

  const {
    collection: groupMetadata,
    collectionLoading: groupMetadataLoading,
    collectionError: groupMetadataError,
  } = useFetchCollection<MetadataFieldWithValue>({
    collectionType: 'metadataGlossaries',
    queryParams: {
      filter_type: 'group_meta',
    },
  });

  const {
    collection: sampleMetadata,
    collectionLoading: sampleMetadataLoading,
    collectionError: sampleMetadataError,
  } = useFetchCollection<MetadataFieldWithValue>({
    collectionType: 'metadataGlossaries',
    queryParams: {
      filter_type: 'sample_meta',
    },
  });

  // This was originally a hook, however this needs to be called conditionally so had to be reworked to a function
  // When this is ported to the Java, it can utilise the ApiService instead
  useEffect(() => {
    const abort = new AbortController();
    if (_notNil(studyId)) {
      fetchStudy(abort.signal);
    }
    return () => {
      abort.abort();
    };
  }, [studyId]);

  const fetchStudy = async (signal: AbortSignal) => {
    setStudyLoading(true);
    try {
      const url = apiRoute('studies.show.p', { id: studyId }, { include: 'users,metadata' });
      const response: any = await Http.get<{ data: Study }>(url, { signal });
      if (isMounted()) {
        setStudy(response?.data?.data ?? response?.data ?? response);
        setStudyLoading(false);
      }
    } catch (error) {
      if (isMounted() && notAborted(error)) {
        setStudyLoading(false);
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const error = groupMetadataError || sampleMetadataError;

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const columnSettings = settings?.tables?.[isStudy ? 'studySamples' : 'samples']?.columns || {};
    if (!groupMetadataLoading && !sampleMetadataLoading) {
      const updatedColumns = buildColumns(groupMetadata, sampleMetadata, columnSettings, isStudy, plannedSamples);
      setColumns(updatedColumns);
    }
  }, [groupMetadataLoading, sampleMetadataLoading, plannedSamples]);

  const updatedSettings = useMemo(() => {
    if (_isNotEmpty(columns)) {
      return updateSettings('samples', columns, settings);
    }
    return settings;
  }, [columns]);

  const filterOptions = generateFilterOptions(users, studyId, sampleMetadata ?? [], plannedSamples);

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const hasAccess = !studyLoading && _notNil(study) && Auth.isWriteUserForStudy(study);

  const handleDelete = async (samples: Array<Sample>, resetTableState: () => void, reasonForChange?: string) => {
    try {
      if (_notNil(study)) {
        const listOfSampleApiIds = samples.map((sample) => sample.api_id as SampleApiId);
        await ApiService.call({
          endpoint: 'DELETE /api/v1/studies/{studyId}/samples/bulk',
          path: { studyId: study.api_id },
          body: {
            sample_api_ids: listOfSampleApiIds,
            ...(_notNil(reasonForChange) ? { reason_for_change: reasonForChange } : {}),
          },
        });
        resetTableState();
        successToast('Successfully deleted!');
      }
    } catch (error) {
      errorToast('There was a problem deleting samples, please try again later.');
    }
  };

  const handleUpdateSampleStatus = async (
    samples: Array<Sample>,
    resetTableState: () => void,
    status: SampleBulkUpdateStatus,
    reasonForChange?: string
  ) => {
    try {
      if (_notNil(study)) {
        const listOfSampleApiIds = samples.map((sample) => sample.api_id as SampleApiId);
        const payload: SampleBulkUpdateStatusV1 = {
          sample_api_ids: listOfSampleApiIds,
          status,
        };

        if (_notNil(reasonForChange)) {
          payload.reason_for_change = reasonForChange;
        }

        await ApiService.call({
          endpoint: 'POST /api/v1/studies/{studyApiId}/planned/samples/update-status' as any,
          path: { studyApiId: study.api_id },
          body: payload,
        });

        resetTableState();

        const actionText = status === 'cancelled' ? 'cancelled' : 'uncancelled';
        successToast(`Successfully ${actionText} ${samples.length} sample${samples.length > 1 ? 's' : ''}!`);
      }
    } catch (error) {
      const actionText = status === 'cancelled' ? 'cancelling' : 'uncancelling';
      errorToast(`There was a problem ${actionText} samples, please try again later.`);
    }
  };

  const handleCancel = async (samples: Array<Sample>, resetTableState: () => void, reasonForChange?: string) => {
    await handleUpdateSampleStatus(samples, resetTableState, 'cancelled', reasonForChange);
  };

  const handleUncancel = async (samples: Array<Sample>, resetTableState: () => void, reasonForChange?: string) => {
    await handleUpdateSampleStatus(samples, resetTableState, 'planned', reasonForChange);
  };

  const bulkActions = ({
    useTableProps: {
      selectedFlatRows,
      apiTable: { resetTableState },
    },
  }: UseTableProps<Sample>) => {
    const selectedSamples = selectedFlatRows.map((sample) => sample.original);
    const { openModal, closeModal } = modalAction(dispatch);

    // For now, bulk actions are only available in the context of a Study
    if (!isStudy || !hasAccess) {
      return [];
    }

    const actions: Array<ActionProps> = [
      {
        name: 'Edit Sample details',
        key: 'edit_details',
        ...validateManageSamplesSelection(hasAccess, selectedSamples),
        action: () => {
          // Sets the selected samples to session, in order to prevent losing context on refresh
          dispatch({ type: 'SET_SELECTED_SAMPLES', selectedSamples });
          sessionStorage.setItem(`selectedStudy${studyId}Samples`, JSON.stringify(selectedSamples));
          sessionStorage.setItem(`selectedStudyApiId${studyId}`, _notNil(study) ? study.api_id : '');
          history.push(webRoute('studies.samples.edit', { id: studyId }));
        },
      },
    ];

    // Show cancel/uncancel based on selection - only when planned_samples flag is enabled
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (plannedSamples) {
      if (selectedSamples.every((sample) => sample.status === 'cancelled')) {
        // All selected samples are cancelled - show only "Uncancel samples"
        actions.push({
          name: 'Uncancel samples',
          key: 'uncancel_samples',
          action: () => {
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (reasonForChange) {
              openModal('CONFIRM_UNCANCEL_SAMPLES', {
                samples: selectedSamples,
                closeModal,
                onClick: (reasonForChange?: string) =>
                  handleUncancel(selectedSamples, resetTableState, reasonForChange),
              });
            } else {
              handleUncancel(selectedSamples, resetTableState);
            }
          },
        });
      } else {
        // Show "Cancel samples" with validation (handles mixed selection or all planned)
        actions.push({
          name: 'Cancel samples',
          key: 'cancel_samples',
          ...validateCancelSamplesSelection(hasAccess, selectedSamples),
          action: () => {
            // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
            if (reasonForChange) {
              openModal('CONFIRM_CANCEL_SAMPLES', {
                samples: selectedSamples,
                closeModal,
                onClick: (reasonForChange?: string) => handleCancel(selectedSamples, resetTableState, reasonForChange),
              });
            } else {
              handleCancel(selectedSamples, resetTableState);
            }
          },
        });
      }
    }

    actions.push({
      name: 'Delete',
      action: () =>
        openModal('CONFIRM_DELETE_SAMPLES', {
          samples: selectedSamples,
          closeModal,
          onClick: (reasonForDelete?: string) => handleDelete(selectedSamples, resetTableState, reasonForDelete),
        }),
      className: 'red',
    });

    return actions;
  };

  return (
    <div className="h-100" data-test-component="SamplesTable" data-test-element="container">
      {/* eslint-disable-next-line @typescript-eslint/strict-boolean-expressions */}
      {error && (
        <ApiErrorBanner
          className="ma3"
          title="There was a problem fetching metadata"
          errorType={ApiErrorType.FETCH}
          error={error}
        />
      )}
      <div data-test-element="table-container">
        <APITable
          columns={columns}
          apiInfo={{
            type: 'legacyInternalApi',
            route: isStudy ? apiRoute('studies.samples.index', { studyId }) : apiRoute('samples.index'),
          }}
          {...(isStudy && { bulkActions })}
          customFilterProcessor={createMultiSelectDoubleEncodingProcessor(sampleMetadata ?? [])}
          includeParam="study_group[metadata],collection,user,study_link"
          filterOptions={filterOptions}
          searchPlaceholderText={'Search by Sample ID'}
          exportRoutes={
            isStudy
              ? [
                  {
                    url: 'samples.export.study',
                    params: {
                      studyId,
                    },
                  },
                ]
              : [
                  {
                    url: 'samples.export',
                  },
                ]
          }
          settings={updatedSettings}
          reduxTableName="samples"
          defaultSortBy={{ id: 'date', desc: true }}
          pageSizes={[50, 100, 200, 500]}
          testIdPrefix="Samples"
        />
      </div>
    </div>
  );
};

export default SamplesTable;
